import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ChevronRight, 
  Clock, 
  Calendar, 
  ChevronDown, 
  ChevronUp,
  CheckCircle2,
  AlertTriangle,
  Lightbulb,
  ArrowRight,
  Droplets,
  FileText,
  Calculator,
  HelpCircle,
  MapPin,
  Layers,
  Gauge,
  Home,
  ShieldCheck,
  Scale
} from 'lucide-react';

const PrixForageEauGuide = () => {
  const [activeSection, setActiveSection] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  // Table of contents
  const sections = [
    { id: 'section-1', title: "Le prix d'un forage en un coup d'œil" },
    { id: 'section-2', title: "Ce qui fait varier le prix" },
    { id: 'section-3', title: "L'étude de sol : l'étape à ne pas sauter" },
    { id: 'section-4', title: "Les démarches obligatoires" },
    { id: 'section-5', title: "Forage ou puits : quelle différence ?" },
    { id: 'section-6', title: "Comment estimer et rentabiliser" },
  ];

  const faqs = [
    {
      question: "Quel est le prix moyen d'un forage d'eau en 2026 ?",
      answer: "Environ 83 € par mètre linéaire en moyenne, dans une fourchette de 50 à 160 €/ml. Pour un projet complet (forage + pompe + raccordement), comptez entre 3 500 et 10 000 €."
    },
    {
      question: "Faut-il une autorisation pour forer un puits chez soi ?",
      answer: "Une déclaration en mairie est obligatoire pour tout forage domestique, au moins un mois avant les travaux, via la plateforme DUPLOS. Au-delà de 10 m, une déclaration au titre du Code minier s'ajoute. Aucun permis de construire n'est requis pour un forage domestique."
    },
    {
      question: "Combien de temps dure un chantier de forage ?",
      answer: "Pour un forage domestique standard d'une trentaine de mètres, il faut généralement compter 2 à 3 jours : installation de la machine, forage, pose du tubage et premiers essais de pompage."
    },
    {
      question: "L'eau d'un forage est-elle potable ?",
      answer: "Pas automatiquement. L'eau souterraine peut contenir des bactéries ou des éléments indésirables. Pour un usage alimentaire, une analyse est indispensable, et un traitement (filtration, stérilisation) peut être nécessaire."
    },
    {
      question: "Faut-il une étude de sol avant de forer ?",
      answer: "C'est fortement recommandé. Elle détermine la présence, la profondeur et la qualité de l'eau, ainsi que le type de sol à traverser. Sans elle, vous risquez de forer sans résultat ou de voir la facture s'envoler."
    }
  ];

  // Track scroll for active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  // Schema.org JSON-LD
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Prix d'un forage d'eau en 2026 : combien ça coûte vraiment ?",
    "description": "Prix d'un forage d'eau en 2026 : coût au mètre, budget complet, équipements, démarches obligatoires (déclaration mairie, DUPLOS). Le guide pour estimer votre projet.",
    "image": "https://geots.consulting/og-images/guide-forage-eau.jpg",
    "author": {
      "@type": "Organization",
      "name": "GEOTS Consulting"
    },
    "publisher": {
      "@type": "Organization",
      "name": "GEOTS Consulting",
      "logo": {
        "@type": "ImageObject",
        "url": "https://geots.consulting/logo.png"
      }
    },
    "datePublished": "2026-07-21",
    "dateModified": "2026-07-21"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Prix d'un forage d'eau en 2026 : combien ça coûte vraiment ? | Guide</title>
        <meta name="description" content="Prix d'un forage d'eau en 2026 : coût au mètre, budget complet, équipements, démarches obligatoires (déclaration mairie, DUPLOS). Le guide pour estimer votre projet." />
        <link rel="canonical" href="https://geots.consulting/guides/prix-forage-eau" />
        <meta property="og:title" content="Prix d'un forage d'eau en 2026 : combien ça coûte vraiment ?" />
        <meta property="og:description" content="Prix d'un forage d'eau en 2026 : coût au mètre, budget complet, équipements, démarches obligatoires." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://geots.consulting/guides/prix-forage-eau" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-[#635BFF]">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/guides" className="hover:text-[#635BFF]">Guides</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0A2540] font-medium">Prix forage d'eau</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <header className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center gap-4 mb-6 flex-wrap">
                <span className="bg-[#00D4FF] text-[#0A2540] text-xs font-bold px-3 py-1 rounded-full">
                  GUIDE PRATIQUE
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Mis à jour le 21 juillet 2026
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  8 min de lecture
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Prix d'un forage d'eau en 2026 :
                <span className="text-[#00D4FF]"> combien ça coûte vraiment ?</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Vous envisagez un forage d'eau pour arroser votre jardin, remplir une piscine ou gagner en autonomie ? 
                Prix au mètre, budget global, équipements, démarches obligatoires — voici tout ce qu'il faut savoir 
                pour estimer votre projet et éviter les mauvaises surprises.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/directory" 
                  className="bg-[#00D4FF] hover:bg-[#00b8e0] text-[#0A2540] px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  Trouver un foreur
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/etude-geotechnique" 
                  className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  Demander une étude de sol
                </Link>
              </div>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sidebar - Table of Contents */}
            <aside className="lg:w-72 flex-shrink-0">
              <div className="sticky top-24 bg-white rounded-xl shadow-sm border p-6">
                <h3 className="font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                  <Droplets className="w-5 h-5 text-[#00D4FF]" />
                  Sommaire
                </h3>
                <nav className="space-y-2">
                  {sections.map((section, index) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-start gap-2 ${
                        activeSection === section.id
                          ? 'bg-[#00D4FF]/10 text-[#00D4FF] font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span className="text-xs font-bold text-gray-400 mt-0.5">{index + 1}.</span>
                      {section.title}
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 max-w-3xl">
              <article className="prose prose-lg max-w-none">
                
                {/* Section 1 - Prix en un coup d'œil */}
                <section id="section-1" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#00D4FF] text-[#0A2540] w-10 h-10 rounded-full flex items-center justify-center text-lg">1</span>
                    Le prix d'un forage d'eau en un coup d'œil
                  </h2>
                  
                  <p className="text-gray-700 mb-6">
                    En 2026, le prix d'un forage d'eau se situe <strong>en moyenne autour de 83 € par mètre linéaire</strong>, 
                    dans une fourchette allant de <strong>50 à 160 € le mètre</strong>, main-d'œuvre comprise.
                  </p>

                  <p className="text-gray-700 mb-6">
                    Pour un <strong>projet complet</strong> — forage, tubage, pompe et raccordement — il faut généralement 
                    prévoir un budget global compris <strong>entre 3 500 et 10 000 €</strong>, selon la profondeur, 
                    le terrain et les équipements.
                  </p>

                  {/* Price Table */}
                  <div className="bg-white rounded-xl border overflow-hidden mb-8">
                    <table className="w-full">
                      <thead className="bg-[#0A2540] text-white">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Poste</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Fourchette de prix 2026</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-[#0A2540]">Forage au mètre linéaire</td>
                          <td className="px-4 py-3 text-[#00D4FF] font-semibold">50 à 160 € / ml (≈ 83 € en moyenne)</td>
                        </tr>
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-[#0A2540]">Forage sur nappe phréatique</td>
                          <td className="px-4 py-3 text-[#00D4FF] font-semibold">50 à 110 € / ml</td>
                        </tr>
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-[#0A2540]">Projet complet (forage + pompe + raccordement)</td>
                          <td className="px-4 py-3 text-[#00D4FF] font-semibold">3 500 à 10 000 €</td>
                        </tr>
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-[#0A2540]">Pompe immergée et équipements</td>
                          <td className="px-4 py-3 text-[#00D4FF] font-semibold">1 250 à 1 750 €</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <p className="text-yellow-800">
                      <strong>Important :</strong> Ces montants sont des ordres de grandeur. Seul un <strong>devis établi 
                      après étude du terrain</strong> donnera un prix fiable pour votre projet précis.
                    </p>
                  </div>
                </section>

                {/* Section 2 - Facteurs de variation */}
                <section id="section-2" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#00D4FF] text-[#0A2540] w-10 h-10 rounded-full flex items-center justify-center text-lg">2</span>
                    Ce qui fait varier le prix
                  </h2>

                  <p className="text-gray-700 mb-6">
                    Le coût d'un forage dépend de plusieurs facteurs, qui expliquent l'écart important entre le bas 
                    et le haut de la fourchette.
                  </p>

                  <div className="space-y-4 mb-8">
                    {[
                      {
                        icon: Layers,
                        title: "La profondeur",
                        desc: "C'est le premier facteur. Plus la nappe est profonde, plus il faut forer, et plus la facture grimpe. Un forage domestique courant se situe souvent autour de 30 mètres, mais certains descendent bien plus bas."
                      },
                      {
                        icon: MapPin,
                        title: "La nature du sol",
                        desc: "Un terrain meuble se fore vite et à moindre coût. Une roche dure exige des outils spécifiques (marteau fond de trou) et plus de temps de main-d'œuvre — ce qui augmente le prix au mètre."
                      },
                      {
                        icon: Gauge,
                        title: "Le diamètre du forage",
                        desc: "Un diamètre plus large coûte plus cher, à la fois en forage et en matériaux (tubage plus important)."
                      },
                      {
                        icon: Calculator,
                        title: "Les équipements",
                        desc: "Pompe immergée ou de surface, ballon, coffret électrique, système de filtration ou de traitement de l'eau : chaque équipement s'ajoute au coût du forage lui-même."
                      },
                      {
                        icon: Home,
                        title: "L'accès au terrain",
                        desc: "Un site difficile d'accès, exigu ou en pente complique l'installation de la machine et peut renchérir le chantier."
                      },
                      {
                        icon: Scale,
                        title: "La région et le prestataire",
                        desc: "Les tarifs varient selon les zones géographiques et les entreprises. D'où l'intérêt de comparer plusieurs devis."
                      },
                    ].map((factor, i) => (
                      <div key={i} className="bg-white rounded-xl border p-5 flex items-start gap-4">
                        <div className="bg-[#00D4FF]/10 p-3 rounded-lg">
                          <factor.icon className="w-6 h-6 text-[#00D4FF]" />
                        </div>
                        <div>
                          <h3 className="font-bold text-[#0A2540] mb-1">{factor.title}</h3>
                          <p className="text-gray-600">{factor.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 3 - Étude de sol */}
                <section id="section-3" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#00D4FF] text-[#0A2540] w-10 h-10 rounded-full flex items-center justify-center text-lg">3</span>
                    L'étude de sol : l'étape à ne pas sauter
                  </h2>

                  <p className="text-gray-700 mb-6">
                    C'est le point que beaucoup de particuliers négligent, et c'est une erreur qui coûte cher.
                  </p>

                  <p className="text-gray-700 mb-6">
                    Avant de forer, il faut savoir <strong>où</strong> et <strong>à quelle profondeur</strong> se trouve l'eau. 
                    Longtemps, on faisait appel à un sourcier — mais aujourd'hui, les foreurs sérieux s'appuient sur des 
                    <strong> données géologiques et hydrogéologiques</strong> pour localiser la nappe et estimer la profondeur à atteindre.
                  </p>

                  <div className="bg-white rounded-xl border p-6 mb-6">
                    <h3 className="text-lg font-bold text-[#0A2540] mb-4">Cette étude préalable détermine :</h3>
                    <ul className="space-y-3">
                      {[
                        "La présence et la profondeur de la nappe",
                        "Le type de sol à traverser (donc le matériel et la méthode de forage)",
                        "La faisabilité réelle du projet"
                      ].map((item, i) => (
                        <li key={i} className="flex items-center gap-3 text-gray-700">
                          <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-[#635BFF]/5 border border-[#635BFF]/20 rounded-xl p-6">
                    <Lightbulb className="w-8 h-8 text-[#635BFF] mb-3" />
                    <p className="text-[#0A2540]">
                      Sans elle, vous forez à l'aveugle — avec le risque de payer pour un forage qui ne donne pas d'eau, 
                      ou qui coûte bien plus cher que prévu parce que le terrain n'était pas celui attendu. 
                      <strong> L'étude de sol est une assurance, pas une dépense superflue.</strong>
                    </p>
                  </div>

                  {/* Link to related guide */}
                  <div className="mt-6 bg-white rounded-xl border p-4 hover:shadow-md transition-shadow">
                    <Link to="/guides/comment-choisir-etude-geotechnique" className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-[#0A2540]">Guide connexe</p>
                        <p className="text-gray-600 text-sm">Comment choisir son étude géotechnique ?</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-[#635BFF]" />
                    </Link>
                  </div>
                </section>

                {/* Section 4 - Démarches obligatoires */}
                <section id="section-4" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#00D4FF] text-[#0A2540] w-10 h-10 rounded-full flex items-center justify-center text-lg">4</span>
                    Les démarches obligatoires (à ne pas négliger)
                  </h2>

                  <p className="text-gray-700 mb-6">
                    Forer chez soi n'est pas totalement libre. La réglementation encadre les forages pour protéger les eaux souterraines.
                  </p>

                  <div className="space-y-4 mb-8">
                    <div className="bg-white rounded-xl border-l-4 border-l-[#635BFF] p-5">
                      <div className="flex items-start gap-3">
                        <FileText className="w-6 h-6 text-[#635BFF] flex-shrink-0 mt-0.5" />
                        <div>
                          <h3 className="font-bold text-[#0A2540] mb-2">Déclaration en mairie obligatoire</h3>
                          <p className="text-gray-600">
                            Depuis 2009, pour tout forage domestique, quelle que soit sa profondeur. La démarche doit être faite 
                            <strong> au moins un mois avant le début des travaux</strong>. Depuis février 2024, elle peut se faire 
                            en ligne via la plateforme <strong>DUPLOS</strong> (mise en place par le BRGM).
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl border-l-4 border-l-orange-500 p-5">
                      <div className="flex items-start gap-3">
                        <Layers className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <h3 className="font-bold text-[#0A2540] mb-2">Au-delà de 10 mètres de profondeur</h3>
                          <p className="text-gray-600">
                            Le forage relève aussi d'une déclaration au titre du <strong>Code minier</strong> — le récépissé 
                            est généralement généré automatiquement via DUPLOS.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl border-l-4 border-l-yellow-500 p-5">
                      <div className="flex items-start gap-3">
                        <Gauge className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <h3 className="font-bold text-[#0A2540] mb-2">Si le prélèvement dépasse 1 000 m³ par an</h3>
                          <p className="text-gray-600">
                            On quitte le cadre de l'usage domestique : le forage relève alors d'un régime de déclaration ou 
                            d'autorisation au titre du Code de l'environnement. Renseignez-vous auprès de la DDT de votre département.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl border-l-4 border-l-green-500 p-5">
                      <div className="flex items-start gap-3">
                        <ShieldCheck className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <h3 className="font-bold text-[#0A2540] mb-2">Si l'eau est destinée à la consommation</h3>
                          <p className="text-gray-600">
                            Une analyse et une information de l'Agence Régionale de Santé (ARS) sont nécessaires.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-red-800">
                      <strong>Attention :</strong> le non-respect de ces obligations expose à des sanctions financières importantes. 
                      La déclaration n'est pas une formalité optionnelle.
                    </p>
                  </div>
                </section>

                {/* Section 5 - Forage vs Puits */}
                <section id="section-5" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#00D4FF] text-[#0A2540] w-10 h-10 rounded-full flex items-center justify-center text-lg">5</span>
                    Forage ou puits : quelle différence ?
                  </h2>

                  <p className="text-gray-700 mb-6">
                    Les deux termes sont souvent confondus :
                  </p>

                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    {/* Puits traditionnel */}
                    <div className="bg-white rounded-xl border p-6">
                      <div className="bg-amber-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                        <span className="text-2xl">🪣</span>
                      </div>
                      <h3 className="text-xl font-bold text-[#0A2540] mb-3">Le puits traditionnel (creusé)</h3>
                      <ul className="space-y-2 text-gray-600 mb-4">
                        <li className="flex items-start gap-2">
                          <span className="text-amber-500 mt-1">•</span>
                          Large (1 à 1,2 m de diamètre)
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-amber-500 mt-1">•</span>
                          Peu profond (souvent moins de 10 m)
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-amber-500 mt-1">•</span>
                          Capte les eaux de surface
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-amber-500 mt-1">•</span>
                          Débit limité et variable selon les saisons
                        </li>
                      </ul>
                      <p className="text-sm bg-amber-50 p-3 rounded-lg">
                        <strong>Budget indicatif :</strong> 2 300 à 8 000 € selon le sol
                      </p>
                    </div>

                    {/* Forage */}
                    <div className="bg-white rounded-xl border-2 border-[#00D4FF] p-6">
                      <div className="bg-[#00D4FF]/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                        <Droplets className="w-6 h-6 text-[#00D4FF]" />
                      </div>
                      <h3 className="text-xl font-bold text-[#0A2540] mb-3">Le forage</h3>
                      <ul className="space-y-2 text-gray-600 mb-4">
                        <li className="flex items-start gap-2">
                          <span className="text-[#00D4FF] mt-1">•</span>
                          Étroit, réalisé à la machine
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-[#00D4FF] mt-1">•</span>
                          Descend chercher les nappes plus profondes
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-[#00D4FF] mt-1">•</span>
                          Débit plus régulier et généralement plus important
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-[#00D4FF] mt-1">•</span>
                          Solution la plus courante aujourd'hui
                        </li>
                      </ul>
                      <p className="text-sm bg-[#00D4FF]/10 p-3 rounded-lg text-[#0A2540]">
                        <strong>Recommandé</strong> pour la plupart des projets
                      </p>
                    </div>
                  </div>

                  <p className="text-gray-700">
                    Le choix dépend de la ressource disponible sous votre terrain — que seule l'étude hydrogéologique permet de déterminer.
                  </p>
                </section>

                {/* Section 6 - Rentabiliser */}
                <section id="section-6" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#00D4FF] text-[#0A2540] w-10 h-10 rounded-full flex items-center justify-center text-lg">6</span>
                    Comment estimer et rentabiliser son forage
                  </h2>

                  <div className="bg-white rounded-xl border p-6 mb-6">
                    <h3 className="text-lg font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                      <Calculator className="w-5 h-5 text-[#635BFF]" />
                      Le retour sur investissement
                    </h3>
                    <p className="text-gray-700">
                      Un forage devient intéressant financièrement dès que votre consommation le justifie — arrosage d'un 
                      grand jardin, piscine, usages extérieurs. Avec un prix de l'eau de ville souvent supérieur à 
                      <strong> 4 €/m³</strong>, l'économie peut être réelle sur le long terme.
                    </p>
                  </div>

                  <div className="bg-white rounded-xl border p-6 mb-6">
                    <h3 className="text-lg font-bold text-[#0A2540] mb-4">Les bons réflexes pour maîtriser le budget</h3>
                    <ul className="space-y-3">
                      {[
                        "Faire réaliser une étude de terrain avant tout",
                        "Demander plusieurs devis détaillés et comparer poste par poste (forage, tubage, pompe, raccordement, essais)",
                        "Vérifier ce qui est inclus : l'évacuation des déblais, les essais de pompage, la mise en service",
                        "Anticiper les coûts annexes : traitement de l'eau si nécessaire (déferrisation, filtration, stérilisation UV), entretien de la pompe"
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-gray-700">
                          <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Link to related guide */}
                  <div className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow">
                    <Link to="/guides/materiel-de-forage" className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-[#0A2540]">Guide connexe</p>
                        <p className="text-gray-600 text-sm">Guide du matériel de forage</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-[#635BFF]" />
                    </Link>
                  </div>
                </section>

                {/* CTA Section - Demande de devis */}
                <section className="bg-gradient-to-br from-[#00D4FF] to-[#0099cc] rounded-2xl p-8 text-white mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Droplets className="w-8 h-8" />
                    <h2 className="text-2xl font-bold">Vous avez un projet de forage d'eau ?</h2>
                  </div>
                  <p className="mb-6 text-white/90 text-lg">
                    Recevez gratuitement des devis de foreurs qualifiés près de chez vous. 
                    Sans engagement, réponse sous 48h.
                  </p>
                  <Link 
                    to="/forage-eau/demande" 
                    className="inline-flex items-center gap-2 bg-white text-[#0A2540] px-8 py-4 rounded-xl font-bold hover:bg-gray-100 transition-colors text-lg shadow-lg"
                  >
                    Trouver un professionnel pour mon forage d'eau
                    <ArrowRight className="w-6 h-6" />
                  </Link>
                </section>

                {/* CTA Section - Info GEOTS */}
                <section className="bg-[#0A2540] rounded-2xl p-8 text-white mb-16">
                  <h2 className="text-2xl font-bold mb-4">Bien préparer son projet de forage</h2>
                  <p className="mb-6 text-white/70">
                    Un forage réussi repose sur trois piliers : une <strong className="text-white">bonne connaissance du sol</strong> en amont, 
                    un <strong className="text-white">matériel adapté</strong> au terrain, et le respect des <strong className="text-white">démarches réglementaires</strong>. 
                    C'est ce qui distingue un forage rentable d'un chantier qui tourne mal.
                  </p>
                  <p className="mb-6 text-white/70">
                    GEOTS met en relation les acteurs de la géotechnique et du forage : entreprises de forage et de sondage, 
                    bureaux d'études, loueurs de matériel de forage et d'instrumentation.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <Link to="/directory" className="bg-[#635BFF] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#5248e5] transition-colors flex items-center gap-2">
                      Annuaire des professionnels
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                    <Link to="/etude-geotechnique" className="bg-white/10 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/20 transition-colors">
                      Demander une étude de sol
                    </Link>
                  </div>
                </section>

                {/* FAQ Section */}
                <section className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <HelpCircle className="w-8 h-8 text-[#00D4FF]" />
                    Questions fréquentes
                  </h2>

                  <div className="space-y-3">
                    {faqs.map((faq, i) => (
                      <div key={i} className="bg-white rounded-xl border overflow-hidden">
                        <button
                          onClick={() => setOpenFaq(openFaq === i ? null : i)}
                          className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-semibold text-[#0A2540]">{faq.question}</span>
                          {openFaq === i ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                        {openFaq === i && (
                          <div className="px-6 pb-4">
                            <p className="text-gray-700">{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>

                {/* Related Guides */}
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#0A2540] mb-6">Pour aller plus loin</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Link 
                      to="/guides/materiel-de-forage"
                      className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                    >
                      <p className="font-medium text-[#0A2540] mb-2">Guide du matériel de forage</p>
                      <span className="text-sm text-[#635BFF] flex items-center gap-1">
                        Lire le guide
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                    <Link 
                      to="/guides/comment-choisir-etude-geotechnique"
                      className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                    >
                      <p className="font-medium text-[#0A2540] mb-2">Comment choisir son étude géotechnique ?</p>
                      <span className="text-sm text-[#635BFF] flex items-center gap-1">
                        Lire le guide
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                  </div>
                </section>

                {/* Footer note */}
                <div className="text-center text-gray-500 text-sm border-t pt-8">
                  <p>
                    Ce guide est fourni à titre informatif. Les prix indiqués sont des ordres de grandeur constatés en 2026 
                    et ne remplacent pas un devis établi après étude du terrain. Les obligations réglementaires doivent être 
                    vérifiées auprès de votre mairie et des services compétents.
                  </p>
                  <p className="mt-2">Dernière mise à jour : 21 juillet 2026</p>
                </div>
              </article>
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrixForageEauGuide;
